import React, { useState, useMemo, useEffect } from 'react';
import { Button, Badge, Spinner } from 'reactstrap';
import UncontrolledDefaultModal from 'components/Modal/UncontrolledDefaultModal';
import useMonitoringQueueGroups from 'hooks/useMonitoringQueueGroups';
import { FlexTableV2 } from 'components/FlexTableV2';
import MonitoringQueueGroupForm from './MonitoringQueueGroupForm';

const MonitoringQueueGroupsManager = ({ isOpen, toggle, onGroupsChanged }) => {
  const [viewMode, setViewMode] = useState('LIST');
  const [editingGroup, setEditingGroup] = useState(null);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [sorting, setSorting] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnSizing, setColumnSizing] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);

  const {
    response: groups,
    totalRegisters,
    isLoading,
    fetch,
    deleteGroup
  } = useMonitoringQueueGroups({ itemsPerPage: pagination.pageSize });

  const nameFilter = columnFilters.find((f) => f.id === 'name')?.value || '';

  useEffect(() => {
    if (isOpen) {
      setViewMode('LIST');
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setColumnFilters([]);
      fetch(1, '');
    }
  }, [isOpen, fetch]);

  useEffect(() => {
    if (viewMode === 'LIST' && isOpen) {
      fetch(pagination.pageIndex + 1, nameFilter);
    }
  }, [pagination.pageIndex, viewMode, isOpen, fetch, nameFilter]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [nameFilter]);

  const handleCreateNew = () => {
    setEditingGroup(null);
    setViewMode('FORM');
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setViewMode('FORM');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja remover este agrupamento?')) {
      const success = await deleteGroup(id);
      if (success) {
        if (onGroupsChanged) onGroupsChanged();
        fetch(pagination.pageIndex + 1, '');
      }
    }
  };

  const handleFormSuccess = () => {
    if (onGroupsChanged) onGroupsChanged();
    setViewMode('LIST');
    fetch(pagination.pageIndex + 1, '');
  };

  const handleFormCancel = () => {
    setViewMode('LIST');
  };

  const allQueuesOptions = useMemo(() => {
    const queueNames = new Set();
    groups.forEach((group) => {
      if (group.queues) {
        group.queues.forEach((q) => {
          const name = typeof q === 'string' ? q : q.name;
          if (name) queueNames.add(name);
        });
      }
    });
    return Array.from(queueNames).sort();
  }, [groups]);

  const columns = useMemo(
    () => [
      {
        header: 'Nome',
        accessorKey: 'name',
        size: 150,
        enableColumnFilter: true
      },
      {
        header: 'Filas',
        accessorKey: 'queues',
        size: 250,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue || filterValue.length === 0) return true;
          const queues = row.getValue(columnId) || [];
          return queues.some((q) => {
            const name = typeof q === 'string' ? q : q.name;
            return filterValue.includes(name);
          });
        },
        meta: {
          filterVariant: 'multiselect',
          options: allQueuesOptions
        },
        cell: ({ row }) => {
          const queues = row.original.queues || [];
          const visibleQueues = queues.slice(0, 3);
          const hiddenCount = queues.length - visibleQueues.length;

          return (
            <div className="d-flex flex-wrap">
              {visibleQueues.map((queue) => (
                <Badge
                  key={queue.id || queue}
                  color="info"
                  pill
                  className="mr-1 mb-1"
                  style={{ fontSize: '0.7rem' }}
                >
                  {queue.name || queue}
                </Badge>
              ))}
              {hiddenCount > 0 && (
                <Badge
                  color="light"
                  pill
                  className="mb-1"
                  style={{ fontSize: '0.7rem' }}
                >
                  +{hiddenCount}
                </Badge>
              )}
            </div>
          );
        }
      },
      {
        id: 'actions',
        header: 'Ações',
        size: 100,
        enableColumnFilter: false,
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              color="info"
              className="mr-2"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.original);
              }}
              title="Visualizar"
            >
              <li className="fas fa-eye"></li>
            </Button>
          </div>
        )
      }
    ],
    [allQueuesOptions]
  );

  const renderBody = () => {
    return (
      <div
        className="px-4"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {viewMode === 'LIST' ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0 text-muted">Total: {totalRegisters}</h5>
            </div>

            {isLoading && !groups.length ? (
              <div className="text-center py-4">
                <Spinner size="sm" />
              </div>
            ) : (
              <FlexTableV2
                state={{
                  pagination,
                  setPagination,
                  sorting,
                  setSorting,
                  columnOrder,
                  setColumnOrder,
                  columnVisibility,
                  setColumnVisibility,
                  columnSizing,
                  setColumnSizing,
                  columnFilters,
                  setColumnFilters,
                  rowCount: totalRegisters
                }}
                table={{
                  data: groups,
                  columns,
                  manualPagination: true,
                  manualFiltering: false,
                  enableLocalStorageMemo: false,
                  hidePaginationPageSelect: true,
                  hidePaginationSizeSelect: true,
                  scrollContainerHeight: '500px'
                }}
              />
            )}
          </>
        ) : (
          <MonitoringQueueGroupForm
            groupData={editingGroup}
            onSuccess={handleFormSuccess}
            onCancel={handleFormCancel}
            readOnly={true}
          />
        )}
      </div>
    );
  };

  const getHeaderTitle = () => {
    if (viewMode === 'LIST') return 'Visualizar Agrupamentos';
    return editingGroup ? 'Visualizar Agrupamento' : 'Novo Agrupamento';
  };

  const customHeader = () => (
    <div className="modal-header">
      <h6 className="modal-title">{getHeaderTitle()}</h6>
      <button
        aria-label="Close"
        className="close"
        data-dismiss="modal"
        type="button"
        onClick={toggle}
      >
        <span aria-hidden={true}>×</span>
      </button>
    </div>
  );

  const customFooter =
    viewMode === 'LIST'
      ? () => (
          <Button
            color="danger"
            className="ml-auto mt-2 mb-4 mr-4"
            onClick={toggle}
          >
            Fechar
          </Button>
        )
      : null;

  return (
    <UncontrolledDefaultModal
      isOpen={isOpen}
      toggle={toggle}
      header={customHeader}
      body={renderBody()}
      footer={customFooter}
      size="xl"
    />
  );
};

export default MonitoringQueueGroupsManager;
